import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PriceMatchPolicy {
  enabled: boolean;
  discountPercentage: number;
  message: string;
  appliesTo: "consumables" | "equipment" | "both";
}

export function usePriceMatchPolicy(productType?: "consumable" | "equipment") {
  const { data: policy, isLoading } = useQuery({
    queryKey: ["price-match-policy"],
    queryFn: async () => {
      const keys = [
        "price_match_enabled",
        "price_match_discount_percentage",
        "price_match_message",
        "price_match_applies_to",
      ];
      const { data, error } = await supabase
        .from("app_settings")
        .select("setting_key, setting_value")
        .in("setting_key", keys);
      if (error) throw error;

      const map: Record<string, string> = {};
      data?.forEach((d) => (map[d.setting_key] = d.setting_value));

      return {
        enabled: map.price_match_enabled === "true",
        discountPercentage: parseFloat(map.price_match_discount_percentage || "5"),
        message:
          map.price_match_message ||
          "Se encontrar mais barato, nós igualamos e ainda fazemos {discount}% de desconto!",
        appliesTo: (map.price_match_applies_to || "both") as PriceMatchPolicy["appliesTo"],
      } satisfies PriceMatchPolicy;
    },
    staleTime: 5 * 60 * 1000,
  });

  const shouldShow = (() => {
    if (!policy?.enabled || !productType) return false;
    if (policy.appliesTo === "both") return true;
    if (policy.appliesTo === "consumables" && productType === "consumable") return true;
    if (policy.appliesTo === "equipment" && productType === "equipment") return true;
    return false;
  })();

  return { policy, isLoading, shouldShow };
}
